// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ImageSourcePropType } from 'react-native';
import { buildMediaUrl } from '@/services/mediaUrl';
import { getAssetImageSource } from '@/services/assetImageSource';

export type GameImageResolver = (
  imagePath?: string | null,
  name?: string | null,
) => ImageSourcePropType;

const FALLBACK_IMAGE = 'images/games/mystery-select.png';

// Canonical artwork per game slug. The media page path is baked into every asset
// URL (it drives media-request telemetry), so the map is built per calling screen
// by createGameImageResolver rather than shared as a module constant.
const GAME_IMAGE_PATH_BY_SLUG: Record<string, string> = {
  'best-ball': 'images/games/best-ball-select.png',
  '3-putts': 'images/games/3-putts-select.png',
  bunker: 'images/games/bunker-select.png',
  'high-low': 'images/games/high-low-select.png',
  mystery: 'images/games/mystery-select.png',
  penalty: 'images/games/penalty-select.png',
  out: 'images/games/out-select.png',
  scramble: 'images/games/scramble-select.png',
  skins: 'images/games/skins-select.png',
  stableford: 'images/games/stableford-select.png',
  'stroke-play': 'images/games/stroke-play-select.png',
  water: 'images/games/water-select.png',
  eclectic: 'images/games/eclectic-select.png',
  freeze: 'images/games/freeze-select.png',
};

function getGameSlug(name?: string | null) {
  const fromName = (name ?? '').trim();
  if (!fromName) {
    return '';
  }
  return fromName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function resolveImageUriFromUnstableAssetPath(raw: string, mediaPagePath: string): string | null {
  try {
    const parsed = new URL(raw, 'http://localhost');
    if (!parsed.pathname.startsWith('/assets/')) {
      return null;
    }

    const unstablePath = parsed.searchParams.get('unstable_path');
    if (!unstablePath) {
      return null;
    }

    let normalized = decodeURIComponent(unstablePath).trim().replace(/\\/g, '/');
    normalized = normalized.replace(/^[./]+/, '');

    if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
      return buildMediaUrl('images', normalized, 'game-select', { pagePath: mediaPagePath });
    }
  } catch {
    return null;
  }

  return null;
}

function resolveGameImageUri(imagePath: string | undefined | null, mediaPagePath: string): string | null {
  const raw = (imagePath ?? '').trim();
  if (!raw) return null;

  const fromUnstableAssetPath = resolveImageUriFromUnstableAssetPath(raw, mediaPagePath);
  if (fromUnstableAssetPath) {
    return fromUnstableAssetPath;
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('/assets/')) {
    // Static Expo asset URLs should not be used for runtime game media.
    // Fall back to canonical image mapping when no stable media key can be derived.
    return null;
  }

  const normalized = raw.replace(/^\/+/, '');

  if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
    return buildMediaUrl('images', normalized, 'game-select', { pagePath: mediaPagePath });
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1).replace(/^\/+/, '');
      if (key.startsWith('assets-generated/images/') || key.startsWith('images/')) {
        return buildMediaUrl('images', key, 'game-select', { pagePath: mediaPagePath });
      }
    }
  }

  return null;
}

const resolverCache = new Map<string, GameImageResolver>();

/**
 * Builds a game-artwork resolver bound to the media page path of the calling screen
 * (e.g. `/round/[roundId]/select-games` or `/tour/[tourId]/edit`). Resolvers are cached
 * per path so the asset map is only materialised once per screen.
 */
export function createGameImageResolver(mediaPagePath: string): GameImageResolver {
  const cached = resolverCache.get(mediaPagePath);
  if (cached) {
    return cached;
  }

  const fallbackSource = getAssetImageSource(FALLBACK_IMAGE, 'game-select', mediaPagePath);
  const imageSourceBySlug: Record<string, ImageSourcePropType> = {};
  Object.entries(GAME_IMAGE_PATH_BY_SLUG).forEach(([slug, path]) => {
    imageSourceBySlug[slug] = getAssetImageSource(path, 'game-select', mediaPagePath);
  });

  const resolver: GameImageResolver = (imagePath, name) => {
    const uri = resolveGameImageUri(imagePath, mediaPagePath);
    if (uri) {
      return { uri };
    }
    const slug = getGameSlug(name);
    if (!slug) return fallbackSource;
    return imageSourceBySlug[slug] ?? fallbackSource;
  };

  resolverCache.set(mediaPagePath, resolver);
  return resolver;
}
